import { useQuery } from "@tanstack/react-query";
import type { Vehicle } from "@autorepair/contracts";
import { vehiclesApi, tenantSettingsApi } from "../lib/api";
import { VIN_FIELD_LABELS } from "../lib/vinDecode";
import { ModernModal, Spinner } from "./ui-modern";

export function VehicleDetailsModal({ vehicle, onClose }: { vehicle: Vehicle; onClose: () => void }) {
  const decodeQuery = useQuery({
    queryKey: ["vin-decode", vehicle.vin],
    queryFn: () => vehiclesApi.decodeVin(vehicle.vin!),
    enabled: !!vehicle.vin,
  });
  // Gates the vehicle-image thumbnail below — off by default per-shop, see
  // tenant_settings.vehicleImageEnabled's own doc comment.
  const tenantSettingsQuery = useQuery({ queryKey: ["tenant-settings"], queryFn: tenantSettingsApi.get });
  const vehicleImageEnabled = tenantSettingsQuery.data?.settings.vehicleImageEnabled ?? false;

  const title = [vehicle.modelYear, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(" ") || "Vehicle Details";

  return (
    <ModernModal title={title} onCancel={onClose}>
      {vehicleImageEnabled && vehicle.imageUrl && (
        <img
          src={vehicle.imageUrl}
          alt=""
          className="mb-4 h-40 w-full rounded-[10px] border border-[#e2e7ee] object-cover"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      )}
      <dl className="grid grid-cols-2 gap-y-2 text-[13.5px]">
        <dt className="text-[#6b7a8f]">License Plate</dt>
        <dd className="text-right text-[#101b2d]">
          {vehicle.licensePlate ?? "—"}
          {vehicle.licensePlateState ? ` (${vehicle.licensePlateState})` : ""}
        </dd>
        <dt className="text-[#6b7a8f]">VIN</dt>
        <dd className="text-right text-[#101b2d]">{vehicle.vin ?? "—"}</dd>
        <dt className="text-[#6b7a8f]">Engine</dt>
        <dd className="text-right text-[#101b2d]">{vehicle.engineDescription ?? "—"}</dd>
        <dt className="text-[#6b7a8f]">Odometer (last known)</dt>
        <dd className="text-right text-[#101b2d]">{vehicle.lastOdometer ?? "—"}</dd>
      </dl>

      {vehicle.vin && (
        <div className="mt-5 border-t border-[#eef1f6] pt-4">
          <h3 className="mb-2 text-[15px] font-bold text-[#071a33]">From the VIN</h3>
          {decodeQuery.isLoading && <Spinner label="Looking up VIN details…" />}
          {decodeQuery.data?.status === "unavailable" && (
            <p className="text-[13.5px] text-[#8b97a8]">VIN lookup is unavailable right now.</p>
          )}
          {decodeQuery.data?.status === "ok" &&
            (() => {
              const { fields } = decodeQuery.data;
              return (
                <dl className="grid grid-cols-2 gap-y-2 text-[13.5px]">
                  {VIN_FIELD_LABELS.filter(([key]) => fields[key]).map(([key, label]) => (
                    <div key={key} className="contents">
                      <dt className="text-[#6b7a8f]">{label}</dt>
                      <dd className="text-right text-[#101b2d]">{String(fields[key])}</dd>
                    </div>
                  ))}
                </dl>
              );
            })()}
        </div>
      )}
    </ModernModal>
  );
}
